import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'
import debounce from 'lodash/debounce'
import lang from '@/lang'
import { useOpenModal } from '@/modules/_helpers/openModal'
import Topbar from '@/modules/base/components/topbar/topbar'
import OAuthConnectView from '@/modules/apps/own/datev/components/oauth-connect-view'
import mainConfig from '@/config'
import DatevService from '../datevService'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { SubTitle, Title } from '@/modules/base/components/texts/index'
import DatevClientSelection from './datev-client-selection'
import DatevTransferView from './datev-transfer-view'
import DatevExportList from './datev-export-list'
import useWebSocketEvent, { EventTypes } from '@/modules/webSocket'
import { ButtonSquare, CancelButton } from '@/modules/base/components/buttons/index'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import IconInfo from '@assets/images/icons/info.svg'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import moment from 'moment'
import { withAsyncData } from '@/utils/withAsyncData'
import datevConfig from '@/modules/apps/own/datev/config'
import request from '@/modules/base/util/request'
import { useNavigate } from 'react-router'
import appConfig from '@/modules/apps/config'

const useStyles = makeStyles((theme) => ({
  container: ({ isDocumentExport }) => ({
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: 600,
    width: '100%',
    height: !isDocumentExport ? `calc(100vh - ${theme.menuHeight}px)` : 'unset',
    overflow: 'hidden',
    background: theme.backgroundColor,
  }),
  content: {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    justifyContent: 'stretch',
    alignItems: 'center',
    width: '100%',
    overflow: 'hidden',
    overflowY: 'auto',
  },
  block: {
    maxWidth: 950,
    width: 950,
    padding: '45px 80px',
    borderLeft: theme.border,
    borderRight: theme.border,
    background: theme.white,
    position: 'relative',
  },
  connectView: {
    padding: '36px 0',
  },
  connectedInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '& > *': {
      paddingTop: 10,
    },
    '& span:first-of-type': {
      fontWeight: theme.fontWeights.medium,
      paddingTop: 0,
    },
    '& a': {
      color: theme.primaryLight,
    },
  },
  clientSelection: {
    paddingTop: 10,
  },
  clientSelectionContainer: {
    width: 550,
    overflowY: 'hidden',
    height: '100%',
    maxHeight: 700,
  },
  clientSelectionTitle: {
    fontWeight: theme.fontWeights.regular,
    color: theme.text,
  },
  transferView: {
    paddingBottom: '40px',
  },
  infoIcon: {
    height: 15,
    width: 15,
    verticalAlign: 'text-top',
    cursor: 'help',
  },
  buttonContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  statusInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    maxWidth: 350,
  },
  buttonDescription: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  selectClientButton: {
    width: 'auto',
    padding: 10,
  },
  clientSelectionContent: {
    overflowY: 'hidden',
    padding: 0,
  },
  clientSelectionActions: {
    padding: 0,
  },
}))

const DatevView = props => {
  const navigate = useNavigate()
  const openModal = useOpenModal()
  const { appId, documentTitle, initConnectionStatus, initAppData, isDocumentExport } = props
  const classes = useStyles(props)
  const [connectionStatus, setConnectionStatus] = useState(initConnectionStatus)
  const [appData, setAppData] = useState(initAppData || { clientId: null })
  const [client, setClient] = useState(null)
  const [transferList, setTransferList] = useState([])

  useWebSocketEvent(EventTypes.DATEV_TRANSFER_JOB_Prefix, (msg) => {
    fetchTransferList()
  })

  const errorCode = {}

  useEffect(() => {
    if (errorCode?.app) {
      errorCode.app.forEach(error => {
        switch (error.code) {
          case 'uninstalled':
          case 'NOT_FOUND':
            toast.showToast({ type: 'error', message: lang.error.notActivated })
            debounce(() => navigate(`/app/all/show/${appId}`), 2000)()
            break
        }
      })
    }
  }, [errorCode])

  useEffect(() => {
    if (connectionStatus?.connected) {
      fetchAppData()
    }
  }, [connectionStatus])

  useEffect(() => {
    if (!isDocumentExport) {
      fetchTransferList()
    }
  }, [])

  const fetchTransferList = () => {
    DatevService.getTransferList()
      .then(data => {
        setTransferList(data)
      })
      .catch(() => toast.showToast({ type: 'error', message: lang.accounting.getTransferListErrorMessage }))
  }

  const fetchAppData = () => {
    DatevService.getAppSettings()
      .then(data => {
        if (!data.clientId) {
          openSelectClientModal()
        }
        setAppData(data)
      })
      .catch(() => toast.showToast({ type: 'error', message: lang.accounting.getSettingsErrorMessage }))
  }

  const openSelectClientModal = () => {
    DatevService.getClients().then(clients => {
      const handleOnChange = (selectedClient, close) => {
        DatevService.updateAppSettings({
          clientId: selectedClient.id,
        }).then(newAppData => {
          setAppData(newAppData)
          setClient(selectedClient)
          close()
          toast.showToast({ message: lang.accounting.saveSettingsSuccessMessage })
        })
      }

      openModal(close => ({
        component: 'ModalSmall',
        children: <DatevClientSelection
          className={classes.clientSelection}
          clients={clients}
          currentClientId={appData.clientId}
          close={close}
          onChange={(selectedClient) => handleOnChange(selectedClient, close)}
        />,
        props: {
          onBackdropClick: close,
          title: lang.accounting.datev.modalTitleClient,
          subtitle: lang.accounting.datev.modalDescriptionClient,
          classes: {
            paper: classes.clientSelectionContainer,
            title: classes.clientSelectionTitle,
            subtitle: classes.clientSelectionTitle,
            content: classes.clientSelectionContent,
            actions: classes.clientSelectionActions,
          },
        },
      }))
    })
  }

  const resetAppData = () => {
    return request(appConfig.api.resetAppData(appId), { method: 'POST', auth: true, isConcurrent: true })
      .then((response) => {
        toast.showToast({ message: lang.accounting.resetConnectionSuccessMessage })
        setAppData({ clientId: null })
        setConnectionStatus({ connected: false })
        return response
      })
      .catch(() => toast.showToast({ type: 'error', message: lang.accounting.resetConnectionErrorMessage }))
  }

  const updateUseDocumentType = (useDocumentType) => {
    return DatevService.updateAppSettings({
      ...appData,
      useDocumentType,
    }).then(newAppData => {
      setAppData(newAppData)
    })
  }

  const updateAutoSyncActive = (autoSyncActive) => {
    return DatevService.updateAppSettings({
      ...appData,
      autoSyncActive,
    }).then(newAppData => {
      setAppData(newAppData)
      toast.showToast({ message: lang.accounting.saveSettingsSuccessMessage })
    })
  }

  const onSubmit = useCallback((period, resend, documentTypes) => {
    return DatevService.postTransfer(appData.clientId, period, resend, documentTypes)
      .then(data => {
        setTransferList(prev => [data, ...prev])
        toast.showToast({ message: lang.accounting.transferSuccessMessage })
        navigate('/document-export#exportList', { replace: true })
      })
      .catch(() => toast.showToast({ type: 'error', message: lang.accounting.transferErrorMessage }))
  }, [appData.clientId])

  useEffect(() => {
    if (appData.clientId && connectionStatus.connected) {
      DatevService.getClient(appData.clientId)
        .then(client => setClient(client))
    }
  }, [appData.clientId, connectionStatus.connected])

  const [consultantNo, clientNo] = appData.clientId?.split('-') || []

  const availableServiceScopes = useMemo(() => client?.services?.flatMap(service => service.scopes) || [], [client])

  const transferView = connectionStatus.connected && appData.clientId && client
    ? (
      <DatevTransferView
        className={classes.transferView}
        onSubmit={onSubmit}
        availableServiceScopes={availableServiceScopes}
        autoSyncActive={appData.autoSyncActive}
        updateAutoSyncActive={updateAutoSyncActive}
        useDocumentType={appData.useDocumentType}
        updateUseDocumentType={updateUseDocumentType}
      />
    )
    : null

  const getStatusInfo = (status, revokeAction, resetAction) => (
    <div className={classes.statusInfo}>
      <div>{lang.accounting.datev.connection.userText(status?.userInfo?.name)}</div>
      <div>{lang.accounting.datev.connection.activeTo}{ moment().add(status.tokenExpiresIn, 'seconds').format(mainConfig.datetimeFormat.dateTime24ss) }</div>
      <div>{lang.accounting.datev.connection.datevAppsUrlText}<a href={datevConfig.endpoints.tokenManagementUrl} target='_blank'>https://apps.datev.de/tokrevui</a></div>
      <div className={classes.buttonContainer}>
        <CancelButton className={classes.revokeButton} label={lang.accounting.datev.buttons.disconnect.label} onClick={revokeAction} />
        <CancelButton className={classes.resetButton} label={lang.accounting.datev.buttons.reset.label} onClick={resetAction} />
      </div>
      <div className={classes.buttonDescription}>
        <div dangerouslySetInnerHTML={{ __html: lang.accounting.datev.buttons.disconnect.description }} />
        <div dangerouslySetInnerHTML={{ __html: lang.accounting.datev.buttons.reset.description }} />
      </div>
    </div>
  )
  const showSelectClientModal = appData && !appData.clientId

  return (
    <div className={classes.container}>
      {!isDocumentExport
        ? <Topbar
          customClass={classes.customerDetailsTopbar}
          title={documentTitle}
          backButtonUrl='/apps'
          variant='white'
        />
        : null }
      <div className={classes.content}>
        <div className={classes.block}>
          <OAuthConnectView
            className={classes.connectView}
            authUrl={datevConfig.endpoints.authUrl}
            longTermScope={'offline_access datev:iam:client:' + client?.id}
            authStatusUrl={datevConfig.endpoints.authStatus}
            revokeUrl={datevConfig.endpoints.revoke}
            revokeAction={DatevService.revoke}
            resetUrl={appConfig.api.resetAppData(appId)}
            resetAction={resetAppData}
            initConnectionStatus={initConnectionStatus}
            providerName='DATEV'
            statusChanged={(status) => { setConnectionStatus(status) }}
            components={{
              StatusInfo: getStatusInfo,
              ConnectedInfo: (providerName, status, revokeAction, resetAction) => (
                <div className={classes.connectedInfo}>
                  <RichTooltip heading={lang.accounting.connectionStateText} body={getStatusInfo(status, revokeAction, resetAction)} width='auto' disabled={!status.connected}>
                    <span>{lang.accounting.connectedText(providerName)} <SVGIcon svg={IconInfo} className={classes.infoIcon} /></span>
                  </RichTooltip>
                  <span>{client && client.name}&nbsp;</span>
                  {!showSelectClientModal && <span>{lang.accounting.datev.currentClientText(consultantNo, clientNo)}</span>}
                  {showSelectClientModal && <ButtonSquare label={lang.accounting.datev.modalTitleClient} className={classes.selectClientButton} onClick={openSelectClientModal} />}
                </div>),
            }}
          />
          {transferView}
          {!isDocumentExport && <DatevExportList className={classes.transferList} data={transferList} />}
        </div>
      </div>
    </div>
  )
}

DatevView.propTypes = {
  appId: PropTypes.string,
  initConnectionStatus: PropTypes.object,
  initAppData: PropTypes.object,
  isDocumentExport: PropTypes.bool,
}

const fetchData = async({ params }) => {
  const data = await Promise.all([
    request(datevConfig.endpoints.authStatus, { auth: true, isConcurrent: true }),
    DatevService.getAppSettings(),
  ]).then(([response, appData]) => {
    const { data } = response.body
    return {
      initConnectionStatus: data,
      appData,
    }
  })

  return Promise.resolve({ ...params, ...data })
}

export default withAsyncData(DatevView, 'Steuerberaterexport', fetchData)
